import { useEffect, useMemo, useState } from "react";

/**
 * Accumulates rows from each page the user opens during the current search session.
 * Resets when {@code resetKey} changes (new search, page size, etc.).
 */
export function useVisitedPageItems<T>(
  pageItems: T[],
  getId: (row: T) => string,
  resetKey: string,
  isSelectable: (row: T) => boolean = () => true,
) {
  const [byId, setById] = useState<Map<string, T>>(() => new Map());

  useEffect(() => {
    setById(new Map());
  }, [resetKey]);

  useEffect(() => {
    if (pageItems.length === 0) return;
    setById((prev) => {
      const next = new Map(prev);
      for (const row of pageItems) {
        next.set(getId(row), row);
      }
      return next;
    });
  }, [pageItems, getId]);

  const allVisited = useMemo(() => [...byId.values()], [byId]);

  const selectableIds = useMemo(
    () => allVisited.filter(isSelectable).map(getId),
    [allVisited, getId, isSelectable],
  );

  return { allVisited, selectableIds };
}
